<template>
  <loading-spinner v-if="state.loading" />
  <div v-else>
    <the-header-card @refresh="loadOrganizationInfo">
      <va-button
        class="ml-2"
        @click="openDeleteModal"
        color="danger"
        icon="delete"
      >
        {{ $t("buttons.delete") }}
      </va-button>
    </the-header-card>

    <div class="flex flex-grow-1 xl16 mb-3">
      <organization-information-tile @editName="showEditNameModal = true" />
    </div>

    <va-button-toggle
      outline
      class="mb-3"
      v-model="state.tableType"
      :options="SWITCH_OPTIONS"
    />
    <organization-users-table
      v-if="state.tableType === 'members-table'"
      ref="membersTableRef"
    />
    <entitlements-table
      v-if="state.tableType === 'entitlements-table'"
      :params="{ ownerId: route.params.id }"
      :owner-id="route.params.id"
    />
  </div>

  <!-- Delete Organization Modal -->
  <delete-organization-modal
    :is-open="state.showDeleteModal"
    :organization-id="route.params.id"
    :organization-name="state.organization?.name || ''"
    @cancel="state.showDeleteModal = false"
    @deleted="handleOrganizationDeleted"
  />

  <!-- Rename Organization Modal -->
  <change-organization-name-modal
    :is-open="showEditNameModal"
    :current-name="state.organization?.name || ''"
    @cancel="showEditNameModal = false"
    @validate="onRenameOrg"
  />
</template>

<script setup>
import { computed, reactive, onMounted, watch, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import TheHeaderCard from "@/components/TheHeaderCard.vue";
import EntitlementsTable from "@/components/entitlements/EntitlementsTable.vue";
import OrganizationUsersTable from "@/components/users/OrganizationUsersTable.vue";
import OrganizationInformationTile from "@/components/organizations/OrganizationInformationTile.vue";
import DeleteOrganizationModal from "@/components/modal/DeleteOrganizationModal.vue";
import ChangeOrganizationNameModal from "@/components/modal/ChangeOrganizationNameModal.vue";
import {
  callAddUserRole,
  callAddMember,
  callFetchUser,
  callAddMemberDirectly
} from "@/api/identity";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const toasts = inject("toasts");
const handleError = inject("handleError");

const SWITCH_OPTIONS = [
  { label: t("members.title"), value: "members-table" },
  { label: t("entitlements.title"), value: "entitlements-table" }
];

const state = reactive({
  loading: false,
  organization: computed(() => store.state.identity.organization),
  tableType: "members-table",
  showDeleteModal: false
});

const membersTableRef = ref(null);
const showEditNameModal = ref(false);

const loadOrganizationInfo = async () => {
  state.loading = true;
  const accountId = route.params.id;
  await store.dispatch("identity/fetchOrganization", accountId);
  state.loading = false;

  // Vérifier si on doit ajouter un owner
  const addOwnerId = route.query.addOwner;
  if (addOwnerId) {
    try {
      console.log("Adding user as member to organization:", addOwnerId);

      try {
        // D'abord récupérer les informations de l'utilisateur directement via l'API
        const userResponse = await callFetchUser(addOwnerId);
        const user = userResponse.data;

        // Récupérer la préférence d'invitation depuis l'URL
        const sendInvitation = route.query.sendInvitation === "true";

        if (sendInvitation) {
          // Utiliser l'endpoint d'invitation
          const memberData = {
            email: user.email,
            senderName: user.name || user.email,
            locale: user.locale || "fr",
            roles: ["OrganizationOwner"]
          };
          await callAddMember(accountId, memberData);
        } else {
          // Utiliser l'endpoint d'ajout direct
          const memberData = {
            roles: ["OrganizationOwner"]
          };
          await callAddMemberDirectly(accountId, user.id, memberData);
        }

        console.log(
          `User added as member with owner role (${
            sendInvitation ? "with" : "without"
          } invitation)`
        );
        toasts.info(
          sendInvitation
            ? t("members.addMember.addMemberWithInvitationSuccessMessage")
            : t("members.addMember.addMemberDirectSuccessMessage")
        );

        // Retirer les paramètres de l'URL
        router.replace({
          path: route.path,
          query: {}
        });

        // Rafraîchir la liste des membres en utilisant la référence
        if (membersTableRef.value) {
          await membersTableRef.value.refreshMembers();
        }
      } catch (error) {
        console.error("Error adding member:", error);
        toasts.error(
          error?.response?.data?.message ||
            t("members.addMember.addMemberErrorMesage")
        );
      }
    } catch (error) {
      console.error("Error adding member:", error);
      toasts.error(error?.response?.data?.message || "Error adding member");
    }
  }
};

onMounted(() => loadOrganizationInfo());

/**
 * Open the delete organization modal
 */
const openDeleteModal = () => {
  state.showDeleteModal = true;
};

/**
 * Handle organization deletion completion
 */
const handleOrganizationDeleted = () => {
  state.showDeleteModal = false;
  // Navigation is handled in the modal
};

/**
 * Handle organization rename submission from modal
 * @param {string} newName - Trimmed new organization name
 */
const onRenameOrg = async (newName) => {
  try {
    await store.dispatch("identity/updateOrganization", { name: newName });
    showEditNameModal.value = false;
    toasts.info(t("toast.organization_updated"));
  } catch (err) {
    handleError(err);
  }
};

// Add a watcher to log changes to tableType
watch(
  () => state.tableType,
  (newVal, oldVal) => {
    console.log(`tableType changed from ${oldVal} to ${newVal}`);
  }
);
</script>

<style scoped>
.flex-grow-1 {
  flex-grow: 1;
}
</style>
